/**
 * Générateur — Automatisme : simplifier une fraction dont numérateur et
 * dénominateur sont dans une même table (15/35, 63/14).
 * Attendu ID : 1222 | Niveau : 3ème | Domaine : Multiples et diviseurs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, lignes 782-783 :
 * « Simplifier une fraction dont le numérateur et le dénominateur sont dans
 * une même table de multiplication, par exemple 15/35 et 63/14. »).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * Le geste de 1051 (5e, « le même nombre en haut et en bas ») à l'envers :
 * ak/bk → a/b en UNE étape, k = LA table commune. Résultat irréductible PAR
 * CONSTRUCTION (a et b premiers entre eux). Frontières : deux étapes et
 * fractions hors tables (140/340 : objectif 3e l. 731, gen_206), produits en
 * croix (155), facteur imposé (156), sens agrandir (1051).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 6NB5
 * option 1 (nbs 1..11 distincts, coeff 2..11, réponse par pgcd) ; retenu :
 * a 1..9 (a = 1 rare, 7/35 = 1/5, À ARBITRER), b 2..10, a ≠ b, a et b
 * premiers entre eux, k 2..10 (k composé admis, 18/30 = 3/5, sous la
 * consigne « Simplifie le plus possible » qui verrouille l'unicité, À
 * ARBITRER), ak et bk ≤ 100 ; fraction > 1 une fois sur quatre (63/14 = 9/2).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes
 *     « 15/35 = ⬚/⬚ » (fraction deux cases, strict), une ligne > 1, une
 *     ligne à k composé, tables distinctes.
 *   Carte 2 « Le même nombre en haut et en bas » (simplifiee_qcm_unique,
 *     nom de 1051) : « 15/35, simplifiée le plus possible, c'est… », QUATRE
 *     choix [3/7 ; 10/30 ; 3/5 ; 3/35] : familles conversion_additive,
 *     deux_coefficients, recopie, chiffres_barres, voisin_meme_table ;
 *     valeurs distinctes de la clé et entre elles.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre égalités vérifiées EN
 *     VALEUR ; dont, quand k est composé, la simplification partielle
 *     « 18/30 = 9/15 » VRAIE (c'est une égalité, pas finie : la ligne de
 *     sens).
 *
 * INVARIANTS : chaque triplet (a, b, k) vérifié (pgcd(a, b) = 1, throw) ;
 * la réponse a/b est irréductible ; tout distracteur est une fraction
 * positive de valeur différente de la clé (memeValeur en entiers).
 * SACRIFIÉ (fiche d'aide « 15 et 35 : tous les deux dans la table de 5 ») :
 * (3 ; 7 ; 5) = 15/35, l'exemple du programme, auto-testé par
 * gen1222.self.test.js ; 63/14 reste en carte.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP direct, DEPP 2de 2025 q.8
 * 1/5 = 5,9 % pour la conversion additive) ────────────────────────────────
 *   conversion_additive : 15/35 → 10/30 (le même nombre RETIRÉ en haut et
 *                         en bas), PRINCIPALE (inférence, À ARBITRER) ;
 *   recopie             : 3/35 (un seul des deux divisé, nom de 1051) ;
 *   deux_coefficients   : 3/5 (÷ 5 en haut, ÷ 7 en bas, nom de 1051) ;
 *   chiffres_barres     : 15/35 → 1/3 (le chiffre commun barré), NEUF ;
 *   voisin_meme_table   : 15/35 → 3/8 (un fait de table voisin).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';
import { gcd, fracTex, memeValeur } from '../../5eme/nombres-rationnels/gen_1054';

const ATTENDU_ID = 1222;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Le même nombre en haut et en bas';
const L_VF = 'Vrai ou faux';

// ─── Le domaine ───────────────────────────────────────────────────────────

/** L'exemple du programme et de la fiche : 15/35 = 3/7 par la table de 5. */
export const TRIPLET_SACRIFIE = { a: 3, b: 7, k: 5 };
const estSacrifie = (a, b, k) => a === TRIPLET_SACRIFIE.a && b === TRIPLET_SACRIFIE.b && k === TRIPLET_SACRIFIE.k;

/** Un item { a, b, k, num, den } : num = ak, den = bk ; `sup1` impose a > b ; `kCompose` impose k composé (4, 6, 8, 9, 10). */
const tirer = ({ sup1 = null, kCompose = null, exclure = [] } = {}) => {
    for (let essai = 0; essai < 300; essai++) {
        const a = randInt(1, 8) === 1 ? 1 : randInt(2, 9);
        const b = randInt(2, 10);
        const k = randInt(2, 10);
        if (a === b || gcd(a, b) !== 1 || a * k > 100 || b * k > 100) continue;
        if (sup1 === true && a < b) continue;
        if (sup1 === false && a > b) continue;
        const compose = [4, 6, 8, 9, 10].includes(k);
        if (kCompose === true && !compose) continue;
        if (kCompose === false && compose) continue;
        if (estSacrifie(a, b, k) || exclure.includes(k)) continue;
        if (gcd(a * k, b * k) !== k) throw new Error(`gen_1222 : pgcd(${a * k}, ${b * k}) ≠ ${k}`);
        return { a, b, k, num: a * k, den: b * k };
    }
    return { a: 9, b: 2, k: 7, num: 63, den: 14 };
};

const EXPLICATIONS = {
    conversion_additive: 'On ne retire pas le même nombre en haut et en bas, on DIVISE par le même nombre : 15 = 3 × 5 et 35 = 7 × 5, donc 15/35 = 3/7.',
    recopie: 'Si je divise le haut par 5, je divise aussi le bas par 5 : 3/7, pas 3/35.',
    deux_coefficients: 'Le même diviseur en haut et en bas : 15 et 35 sont tous les deux dans la table de 5.',
    chiffres_barres: 'On ne barre pas les chiffres : 15/35 n\'est pas 1/3. On cherche une table commune : 5.',
    voisin_meme_table: 'Je vérifie le fait de table : 35 = 5 × 7, donc 15/35 = 3/7.',
    partielle: '9/15 est égal à 18/30, mais 9 et 15 sont encore dans la table de 3 : le plus possible, c\'est 3/5.',
    regle: 'Je cherche une table où sont le haut et le bas, je divise les deux par ce nombre, et je recommence si je peux encore.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

/** Les candidats d'un item, chacun avec sa valeur, positifs, jamais la clé ni deux fois la même valeur. */
const candidats = (it) => {
    const { a, b, k, num, den } = it;
    const c = [];
    const pousser = (code, n, d) => { if (n >= 1 && d >= 1) c.push({ code, val: { num: n, den: d } }); };
    pousser('conversion_additive', num - k, den - k);
    pousser('recopie', a, den);
    pousser('recopie', num, b);
    const autres = [2, 3, 4, 5, 6, 7, 8, 9, 10].filter((d) => d !== k && den % d === 0);
    if (autres.length) pousser('deux_coefficients', a, den / randomChoice(autres));
    const chiffreCommun = [...String(num)].find((ch) => String(den).includes(ch));
    if (chiffreCommun !== undefined && String(num).length === 2 && String(den).length === 2) {
        pousser('chiffres_barres', +String(num).replace(chiffreCommun, ''), +String(den).replace(chiffreCommun, ''));
    }
    pousser('voisin_meme_table', a, b + 1);
    pousser('voisin_meme_table', a + 1, b);
    const vus = [{ num: a, den: b }];
    return c.filter((x) => {
        if (vus.some((v) => memeValeur(v, x.val))) return false;
        vus.push(x.val);
        return true;
    });
};

/** Trois distracteurs de familles distinctes ; null s'il en manque. */
const distracteurs = (it) => {
    const pris = [];
    for (const x of shuffle(candidats(it))) if (!pris.some((y) => y.code === x.code)) pris.push(x);
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const CONSIGNE_FLASH = '<p><b>Simplifie le plus possible.</b></p>';
const ligneFlash = ({ num, den, a, b }) => `${fracTex(num, den)} = \\dfrac{{{${a}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${b}}}}`;
const phraseFlash = (items) => [items.slice(0, 2), items.slice(2, 4)].map((paire) => `$${paire.map(ligneFlash).join(' \\quad ; \\quad ')}$`).join('\n');

const genFlash = () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [];
        items.push(tirer({ sup1: true }));
        // une seule fraction > 1 par série (la première)
        items.push(tirer({ sup1: false, kCompose: true, exclure: items.map((it) => it.k) }));
        items.push(tirer({ sup1: false, kCompose: false, exclure: items.map((it) => it.k) }));
        items.push(tirer({ sup1: false, exclure: items.map((it) => it.k) }));
        if (new Set(items.map((it) => it.k)).size < 4) continue;
        const payload = makeTexteTrou(phraseFlash(shuffle(items)), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, partielle: EXPLICATIONS.partielle } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [{ a: 9, b: 2, k: 7, num: 63, den: 14 }, { a: 3, b: 5, k: 6, num: 18, den: 30 }, { a: 2, b: 9, k: 3, num: 6, den: 27 }, { a: 4, b: 5, k: 2, num: 8, den: 10 }];
    const payload = makeTexteTrou(phraseFlash(items), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2 : QCM ────────────────────────────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const it = tirer();
        const faux = distracteurs(it);
        if (!faux) continue;
        const bon = `$${fracTex(it.a, it.b)}$`;
        const choix = [bon, ...faux.map((f) => `$${fracTex(f.val.num, f.val.den)}$`)];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>${tex(fracTex(it.num, it.den))}, simplifiée le plus possible, c'est…</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 63/14 = 9/2 ; 56/7 (conversion_additive), 9/14 (recopie), 9/1 → 9/3 (deux_coefficients : 14 ÷ 7 → 2 ; on prend 9/7 voisin).
    const bon = `$${fracTex(9, 2)}$`;
    const payload = makeQcm([bon, `$${fracTex(56, 7)}$`, `$${fracTex(9, 14)}$`, `$${fracTex(9, 3)}$`], bon, `<p><b>${tex(fracTex(63, 14))}, simplifiée le plus possible, c'est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { a: 9, b: 2, k: 7, num: 63, den: 14, distracteurs: [{ code: 'conversion_additive' }, { code: 'recopie' }, { code: 'voisin_meme_table' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

/** Une égalité « ak/bk = … » vraie (a/b, ou une simplification partielle quand k est composé), ou fausse par une famille. */
const ligneVf = (it, vraie) => {
    const { a, b, k, num, den } = it;
    if (vraie) {
        // jamais 15/35 même en écriture intermédiaire (30/70 = 15/35)
        const diviseurs = [2, 3, 5].filter((d) => d !== k && k % d === 0 && !(num / d === TRIPLET_SACRIFIE.a * TRIPLET_SACRIFIE.k && den / d === TRIPLET_SACRIFIE.b * TRIPLET_SACRIFIE.k));
        if (diviseurs.length && randomChoice([true, false])) {
            const d = randomChoice(diviseurs);
            return { ligne: tex(`${fracTex(num, den)} = ${fracTex(num / d, den / d)}`), code: null, partielle: true };
        }
        return { ligne: tex(`${fracTex(num, den)} = ${fracTex(a, b)}`), code: null };
    }
    const cands = candidats(it);
    if (!cands.length) return null;
    const x = randomChoice(cands);
    return { ligne: tex(`${fracTex(num, den)} = ${fracTex(x.val.num, x.val.den)}`), code: x.code };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const items = [];
        items.push(tirer({ kCompose: true }));
        for (let i = 1; i < 4; i++) items.push(tirer({ exclure: items.map((it) => it.k) }));
        if (new Set(items.map((it) => it.k)).size < 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => ligneVf(it, verites[i]));
        if (lignes.some((l) => !l) || new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 18/30 = 9/15 V (partielle) ; 63/14 = 9/2 V ; 6/27 = 3/24 F (conversion_additive) ; 8/10 = 4/10 F (recopie).
    const lignes = [tex(`${fracTex(18, 30)} = ${fracTex(9, 15)}`), tex(`${fracTex(63, 14)} = ${fracTex(9, 2)}`), tex(`${fracTex(6, 27)} = ${fracTex(3, 24)}`), tex(`${fracTex(8, 10)} = ${fracTex(4, 10)}`)];
    const payload = makeVraiFaux(lignes, [0, 0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], verites: [true, true, false, false], distracteurs: [{ code: 'conversion_additive' }, { code: 'recopie' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix : la clé a/b (irréductible par construction) et trois familles distinctes, valeurs distinctes de la clé et entre elles',
        'la simplification partielle (18/30 → 9/15) n\'est JAMAIS un distracteur (même valeur que la clé) : elle est une ligne VRAIE du Vrai/Faux',
        'sacrifié : (3 ; 7 ; 5) = 15/35',
    ],
    qcm_unique: {
        support: 'a 1..9 (1 rare), b 2..10, premiers entre eux, k 2..10 composé admis, ak et bk ≤ 100, > 1 une fois sur quatre',
        familles: {
            conversion_additive: '(ak − k)/(bk − k) : le même nombre retiré, la principale',
            recopie: 'a/bk ou ak/b : un seul des deux divisé',
            deux_coefficients: 'a sur bk ÷ (un autre diviseur de bk)',
            chiffres_barres: 'le chiffre commun barré (15/35 → 1/3), à deux chiffres seulement',
            voisin_meme_table: 'a/(b + 1) ou (a + 1)/b',
        },
    },
    vf_tableau_vf: 'quatre tables distinctes, un k composé ; lignes vraies (a/b, ou partielle), fausses par une famille tirée parmi les candidats de l\'item',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    simplifiee_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'simplifiee', label: L_QCM, formatsList: [{ key: 'simplifiee_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
